import React, { useEffect, useState } from "react"
import { Alert, Button, TableColumnsType, Tag, Typography } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useRegister } from "../../../contexts/RegisterContext"
import RegisterView from "../RegisterView"
import { textNumberFormater } from "../../../utils"
import { useItems, useMovements } from "../../../hooks/useInventory"
import useAuth from "../../../hooks/useAuth"
import { useConfig } from "../../../contexts/ConfigContext"
import { useCompany } from "../../../contexts/CompanyContext"
import { movementStatus } from "../../../config/const"
import SelectMovementType from "../../common/SelectMovementType"
import { useUser } from "../../../contexts/UserContext"

const { Text } = Typography

function ViewRegisterEntry(){
  const {register,updateRegister} = useRegister()
  const {config} = useConfig()
  const {company} = useCompany()
  const {user} = useUser()
  const [spinning,setSpinning] = useState(true)
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  const {data:mvData, mutate: mutateMovements} = useMovements(tokenData,{
    company:company.current?.uid,
    module:config.module,
    first: true,
    createdBy: user.document_number,
    status:movementStatus.PENDING,
    documentType: register.currentMovementType
  })
  const {data:itData} = useItems(tokenData,{
    company:company.current?.uid,
    module:config.module,
    filter: false
  })

  useEffect(() => {
    if (tokenData?.key && company.current?.uid && register.currentMovementType) {
      mutateMovements();
    }
  }, [register.currentMovementType]);

  useEffect(()=>{
    if(tokenData && mvData?.movement){
      setSpinning(false)
      const serverMovement = mvData.movement
      if(serverMovement?.uid){
        const isResumedDifferentPending = 
          register.currentPending?.uid && 
          register.currentPending.uid !== serverMovement.uid &&
          register.currentPending.document_type === register.currentMovementType

        const activePending = isResumedDifferentPending 
          ? register.currentPending 
          : serverMovement

        const itemListEntries = activePending.item_list || []
        console.log("entries:", itemListEntries)
        updateRegister({itemListEntries,currentPending: activePending})
      }else if(register.currentPending?.uid && register.currentPending.document_type === register.currentMovementType){
        const itemListEntries = register.currentPending.item_list || []
        updateRegister({itemListEntries,currentPending: register.currentPending})
      }else if(!register.currentPending?.uid){
        updateRegister({itemListEntries:[]})
      }
    } else if (mvData && !mvData.movement) {
      setSpinning(false)
      if (!register.currentPending?.uid) {
        updateRegister({itemListEntries:[]})
      }
    }
    if(tokenData && itData?.itemList){
      updateRegister({registerList:itData.itemList})
    }
  },[tokenData,mvData,itData,register.currentMovementType])

  const columnsEntry:TableColumnsType = [
    {title:"#",dataIndex:"register"},
    {title:"ID",dataIndex:"code"},
    {title:"Nombre",dataIndex:"name", render: (text, record: any) => {
      const name = record.item_name || text
      const regType = record.register_type
      return (
        <span>
          {name}
          {regType === 'SERVICE' && <Tag color="geekblue" style={{ marginLeft: 6, fontSize: 10 }}>Servicio / Costo</Tag>}
          {regType === 'PRODUCT' && <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>Mercancía</Tag>}
          {regType === 'FIXED_ASSET' && <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>Activo Fijo</Tag>}
        </span>
      )
    }},
    {title:"Presentación",dataIndex:"display", render: (text, record: any) => {
      if (record.register_type === 'SERVICE') return <Tag color="default">Servicio</Tag>
      return text || record?.presentation || "UND"
    }},
    {title:"Cantidad",dataIndex:"quantity"},
    {title:"Total",dataIndex:"client_amount",render:(text)=><label className="numeric-values-data-table">{textNumberFormater(text)}</label>},
  ]

  let subTotal = 0
  let inventoryTotal = 0
  let servicesTotal = 0

  const currentMovementType = register.movementTypesEntry.find(el => el.key === register.currentMovementType)
  if(!register.currentMovementType || !currentMovementType){
    return <SelectMovementType />
  }

  const itemList = register.itemListEntries?.map((el)=>{
    const client_amount = el.total_amount_net || el.total_amount_full || 0
    subTotal += client_amount
    if (el.track_inventory === false || el.register_type === 'SERVICE') {
      servicesTotal += client_amount
    } else {
      inventoryTotal += client_amount
    }
    const code = el.custom_id || el.key || el.uid
    return {
      ...el,
      code,
      client_amount
    }
  }) || []

  const isPurchase = register.currentMovementType === "PURCHASE_RECEIPT"
  const isPreparatory = register.currentMovementType === "PURCHASE_ORDER" || register.currentMovementType === "PURCHASE_REQUEST"

  const footerElement = () => (
    <Button 
      type="primary" 
      icon={<PlusOutlined />} 
      onClick={() => updateRegister({ showAddItem: true, initialItemTypeTab: 'ALL' })}
    >
      Añadir Elemento
    </Button>
  )

  const title = isPurchase ? "Factura de Compra (Mercancía y Servicios)" : `Registrar ${currentMovementType.string_value}`

  const topBanner = isPurchase ? (
    <Alert
      message={<Text strong style={{ fontSize: 13 }}>Registro de Compras: Mercancía para Almacén y Servicios / Costos Asociados</Text>}
      description="En esta factura puedes registrar tanto elementos que ingresan físicamente a almacén (productos, insumos, materias primas) como servicios o costos asociados (flete, transporte, mano de obra, descargue)."
      type="info"
      showIcon
      closable
      style={{ marginBottom: 16, borderRadius: 8 }}
    />
  ) : isPreparatory ? (
    <Alert
      message={<Text strong style={{ fontSize: 13 }}>Documento Comercial / Preparatorio</Text>}
      description={`Este registro formaliza una ${currentMovementType.string_value.toLowerCase()} ante el proveedor. No afectará ni incrementará las existencias físicas en bodega hasta que se efectúe la recepción física de la mercancía.`}
      type="warning"
      showIcon
      closable
      style={{ marginBottom: 16, borderRadius: 8 }}
    />
  ) : null

  const addPending = (acept:boolean) => {
    if(register.itemListEntries.length === 0){
      return alert("Debe añadir elementos al registro")
    }
    if(acept){
      updateRegister({showCreateMovement: true})
    }else{
      updateRegister({itemListEntries: [],currentMovementItem:undefined})
    }
  }

  const handleClick = (data:any)=>{
    console.log("Click en:",data)
    updateRegister({showAddItem:true,currentMovementItem:data})
  }
  const headerElement = () => 
  <div style={{display:"flex",justifyContent:"flex-end"}}>
    <Button 
      type="link" 
      onClick={()=>updateRegister({currentMovementType:null})}
    >
      {`Cambiar tipo - ${currentMovementType.string_value}`}
    </Button>
  </div> 

  return (
    <RegisterView
      title={title}
      topBanner={topBanner}
      spinning={spinning}
      columnsData={columnsEntry} 
      itemList={itemList}
      subTotal={subTotal}
      inventoryTotal={inventoryTotal}
      servicesTotal={servicesTotal}
      footerElement={footerElement}
      addPending={addPending}
      headerElement={headerElement}
      actionButtonText={
        register.currentMovementType === 'PURCHASE_REQUEST'
          ? "Generar Solicitud"
          : register.currentMovementType === 'PURCHASE_ORDER'
          ? "Generar Orden"
          : "Registrar Entrada"
      }
      onRow={(data:any)=>{return {onClick:()=>handleClick(data),style:{cursor:"pointer"}}}}>
    </RegisterView>
  )
}

export default ViewRegisterEntry